import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { LogoStacked } from '../components/Logo';

function pwStrength(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (pw.length < 8) return 0;
  let s: 0 | 1 | 2 | 3 | 4 = 1;
  if (pw.length >= 10) s = 2;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw)) s = 3;
  if (/[^A-Za-z0-9]/.test(pw) && pw.length >= 10) s = 4;
  return s;
}

export function Signup() {
  const [params] = useSearchParams();
  const plan = params.get('plan') ?? 'nest';
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [coveName, setCoveName] = useState('');
  const [dob, setDob] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const strength = useMemo(() => pwStrength(pw), [pw]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.signup({
        email: email.trim().toLowerCase(),
        password: pw,
        displayName: displayName.trim(),
        coveName: coveName.trim(),
        dobIso: dob,
      });
      setSent(true);
    } catch (e: any) {
      setErr(e.message || 'signup failed');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="app">
        <div className="auth-hero">
          <LogoStacked />
          <h1 className="display auth-title">Check your email.</h1>
          <p className="muted auth-sub">
            We sent a verification link to <strong>{email}</strong>. Click it to activate your cove.
          </p>
          <p className="auth-footnote">
            No email? Check spam, or{' '}
            <button
              className="link-btn"
              onClick={() => api.resendVerify(email).catch(() => {})}
            >
              resend
            </button>
            .
          </p>
        </div>
      </div>
    );
  }

  const validAge = /^\d{4}-\d{2}-\d{2}$/.test(dob);
  const canSubmit =
    email.includes('@') &&
    pw.length >= 8 &&
    displayName.trim().length > 0 &&
    coveName.trim().length > 0 &&
    validAge &&
    !busy;

  return (
    <div className="app">
      <div className="auth-hero">
        <LogoStacked />
        <p className="eyebrow">Start your cove · {plan}</p>
        <h1 className="display auth-title">Claim your family cove.</h1>
        <p className="muted auth-sub">Only your family. No public directory. No ads.</p>

        <form className="auth-form" onSubmit={submit}>
          <label className="field">
            <span className="field-label">Your email</span>
            <input
              className="input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoCapitalize="none"
            />
          </label>
          <label className="field">
            <span className="field-label">Choose a password</span>
            <input
              className="input"
              type="password"
              placeholder="at least 8 characters"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
            />
            <div className="pw-strength">
              {[1, 2, 3, 4].map((i) => (
                <span key={i} className={strength >= i ? 'on' : ''} />
              ))}
            </div>
          </label>
          <label className="field">
            <span className="field-label">Your name</span>
            <input
              className="input"
              placeholder="Ana Alvarez"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              autoCapitalize="words"
            />
          </label>
          <label className="field">
            <span className="field-label">Your cove's name</span>
            <input
              className="input"
              placeholder="The Alvarez family"
              value={coveName}
              onChange={(e) => setCoveName(e.target.value)}
              autoCapitalize="words"
            />
          </label>
          <label className="field">
            <span className="field-label">Your date of birth</span>
            <input
              className="input"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date(Date.now() - 18 * 365.25 * 86400_000).toISOString().slice(0, 10)}
            />
          </label>
          {err && <div className="error">{err}</div>}
          <button className="btn block" type="submit" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create my cove'}
          </button>
        </form>

        <p className="auth-footnote">
          Already have a cove? <Link to="/login">Sign in</Link>.
        </p>
      </div>
    </div>
  );
}
